import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../supabaseClient';

// ── Icons ──────────────────────────────────────────────────────────────────────

function StarRating({ count }) {
  return (
    <div className="flex justify-center gap-1" aria-label={`${count} out of 5 stars`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <svg
          key={i}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
          className="w-6 h-6"
          style={{ color: i < count ? '#F7941D' : 'rgba(255,255,255,0.2)' }}
        >
          <path
            fillRule="evenodd"
            d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.006 5.404.434c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.434 2.082-5.006z"
            clipRule="evenodd"
          />
        </svg>
      ))}
    </div>
  );
}

function ChevronLeft() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7">
      <path fillRule="evenodd" d="M7.72 12.53a.75.75 0 010-1.06l7.5-7.5a.75.75 0 111.06 1.06L9.31 12l6.97 6.97a.75.75 0 11-1.06 1.06l-7.5-7.5z" clipRule="evenodd" />
    </svg>
  );
}

function ChevronRight() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7">
      <path fillRule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 4.03a.75.75 0 011.06-1.06l7.5 7.5z" clipRule="evenodd" />
    </svg>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

function Testimonials() {
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [index, setIndex]     = useState(0);
  const [paused, setPaused]   = useState(false);
  const touchStartX           = useRef(null);
  const timerRef              = useRef(null);

  useEffect(() => {
    async function fetchTestimonials() {
      const { data } = await supabase
        .from('testimonials')
        .select('quote, author, author_info, stars')
        .eq('visible', true);
      setItems(data || []);
      setLoading(false);
    }
    fetchTestimonials();
  }, []);

  // Keep index in range whenever items changes
  useEffect(() => {
    if (items.length > 0) setIndex(i => Math.min(i, items.length - 1));
  }, [items]);

  // Auto-advance every 10 seconds, pause on hover
  const startTimer = useCallback(() => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setIndex(i => (i + 1) % items.length);
    }, 10000);
  }, [items.length]);

  useEffect(() => {
    if (items.length <= 1 || paused) {
      clearInterval(timerRef.current);
      return;
    }
    startTimer();
    return () => clearInterval(timerRef.current);
  }, [items.length, paused, startTimer]);

  const prev = () => { setIndex(i => (i - 1 + items.length) % items.length); startTimer(); };
  const next = () => { setIndex(i => (i + 1) % items.length); startTimer(); };

  const onTouchStart = e => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd   = e => {
    if (touchStartX.current === null) return;
    const delta = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(delta) > 40) delta > 0 ? next() : prev();
    touchStartX.current = null;
  };

  // Don't render the section at all while loading or if there are no testimonials
  if (loading || items.length === 0) return null;

  const { stars, quote, author, author_info } = items[index];

  return (
    <section
      className="relative w-full px-6 flex items-center justify-center"
      style={{ minHeight: '600px', height: '600px' }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: "url('/Assets/Testimonial-BG[01].jpg')" }}
        aria-hidden="true"
      />
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black/10" aria-hidden="true" />

      <div className="relative z-10 max-w-3xl w-full mx-auto flex flex-col items-center gap-10">
        {/* Carousel */}
        <div
          className="w-full flex items-center gap-4"
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          {/* Left arrow — hidden when only one item */}
          <button
            onClick={prev}
            aria-label="Previous testimonial"
            className="shrink-0 text-gold hover:text-white transition-colors duration-200"
            style={{ visibility: items.length > 1 ? 'visible' : 'hidden' }}
          >
            <ChevronLeft />
          </button>

          {/* Slide */}
          <div className="flex-1 flex flex-col items-center gap-5">
            <StarRating count={stars ?? 5} />
            <blockquote className="font-source-serif italic text-white text-base sm:text-lg md:text-xl leading-relaxed text-left">
              &ldquo;{quote}&rdquo;
            </blockquote>
            <div className="text-left w-full flex flex-col gap-0.5">
              <p className="font-poppins text-white text-sm font-bold">
                {author}
              </p>
              {author_info && (
                <p className="font-poppins text-xs" style={{ color: '#CCCCCC', fontWeight: 300 }}>
                  {author_info}
                </p>
              )}
            </div>
          </div>

          {/* Right arrow */}
          <button
            onClick={next}
            aria-label="Next testimonial"
            className="shrink-0 text-gold hover:text-white transition-colors duration-200"
            style={{ visibility: items.length > 1 ? 'visible' : 'hidden' }}
          >
            <ChevronRight />
          </button>
        </div>

        {/* Dot indicators — only if more than one */}
        {items.length > 1 && (
          <div className="flex gap-2">
            {items.map((_, i) => (
              <button
                key={i}
                onClick={() => { setIndex(i); startTimer(); }}
                aria-label={`Go to testimonial ${i + 1}`}
                className={`w-2 h-2 rounded-full transition-colors duration-200 ${
                  i === index ? 'bg-gold' : 'bg-white/40'
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export default Testimonials;
